import * as React from 'react';
import 'twin.macro';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { Controller, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';

import { config } from '@restworld/utils-common';
import { Checkbox } from '@restworld/ui-ds-base';

import { RestaurantOnboardingFormSection, RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import EmailFieldWithSuggestions from './email-field-with-suggestions';
import {
  isOrgOnboardingModeAtom,
  orgOnboardingSaveAndContinueLaterAtom,
} from '../../organization-onboarding/use-organization-onboarding';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IRestaurantOrganizationOnboardingContactFormProps {}

const AgreementCheckbox: React.FC<{
  textKey: string;
  link: string;
  linkKey: keyof Pick<RestaurantOnboardingPayload, 'privacy_policy' | 'terms_and_conditions'>;
}> = ({ textKey, link, linkKey }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <div id={linkKey} tw="flex flex-col gap-4">
      <Controller
        control={control}
        name={linkKey}
        rules={{ required: t('this_is_a_required_field') }}
        render={({ field }) => (
          <Checkbox
            id={linkKey}
            title={
              <label
                htmlFor={linkKey}
                className={
                  errors[linkKey]
                    ? 'text-red-600 text-base font-medium'
                    : 'text-neutral-700 text-base font-medium cursor-pointer'
                }
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    field.onChange(!field.value);
                  }}
                >
                  {t(textKey)}{' '}
                </span>
                <Link href={link} target="_blank" rel="noopener noreferrer">
                  <span tw="underline">{t(linkKey)}</span>
                </Link>
              </label>
            }
            isChecked={field.value}
            iserror={!!errors[linkKey]}
            {...field}
          />
        )}
      />
      {/* {errors[linkKey] && <HelperTextRElement helperText={errors[linkKey].message.toString()} />} */}
    </div>
  );
};

const RestaurantOrganizationOnboardingContactForm: React.FunctionComponent<
  IRestaurantOrganizationOnboardingContactFormProps
> = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const isSaveAndContinueLater = useAtomValue(orgOnboardingSaveAndContinueLaterAtom);

  const headerText = React.useMemo(() => {
    if (!isOrgOnboarding) return t('almost_done');
    if (isSaveAndContinueLater) return t('enter_your_contact_information_to_save_the_ad');
    return t('enter_your_contact_information_to_post_the_ad');
  }, [isOrgOnboarding, isSaveAndContinueLater, t]);

  return (
    <div tw="inline-flex flex-col justify-start items-start gap-12 h-full w-full">
      <div tw="self-stretch flex flex-col justify-start items-start gap-4">
        <h5 tw="justify-start text-neutral-700 text-xl font-semibold">{headerText}</h5>
        {!isOrgOnboarding && (
          <h5 tw="self-stretch justify-start text-neutral-200 text-sm font-medium">
            {t('input_your_contact_to_complete_the_registration')}
          </h5>
        )}
      </div>
      <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6">
        <RestaurantOnboardingFormSection
          label={t('name')}
          placeholder=""
          name="name"
          required={t('name_is_required')}
        />
        <RestaurantOnboardingFormSection
          label={t('surname')}
          placeholder=""
          name="surname"
          required={t('surName_is_required')}
        />
        <EmailFieldWithSuggestions label={t('email')} required={t('enter_valid_email')} />
        <RestaurantOnboardingFormSection
          label={t('phone_number')}
          placeholder=""
          name="phone_number"
          required={t('enter_valid_phone_number')}
        />
      </div>
      <div tw="self-stretch inline-flex flex-col justify-start items-start gap-8">
        <AgreementCheckbox
          textKey="i_read_and_agree_to_restworlds"
          link={config.restworldPrivacyUrl}
          linkKey="privacy_policy"
        />
        <AgreementCheckbox
          textKey="i_read_and_agree_to_restworlds"
          link={config.employerTermsUrl}
          linkKey="terms_and_conditions"
        />
      </div>
    </div>
  );
};

export default RestaurantOrganizationOnboardingContactForm;
